import type { DocumentSnapshot } from "../../../infrastructure/documents/document-engine.js";
import { AppError } from "../../../shared/errors/app-error.js";

class PdfPreview extends HTMLElement {
  private _snapshot: DocumentSnapshot | null = null;
  private _pdfBytes: Uint8Array | null = null;
  private _objectUrl: string | null = null;
  private isGeneratingPdf: boolean = false;
  private pdfError: string | null = null;

  set snapshot(val: DocumentSnapshot) {
    this._snapshot = val;
    this.generatePdf();
  }

  get snapshot(): DocumentSnapshot | null {
    return this._snapshot;
  }

  disconnectedCallback() {
    if (this._objectUrl) {
      URL.revokeObjectURL(this._objectUrl);
      this._objectUrl = null;
    }
  }

  private async generatePdf() {
    if (!this._snapshot || this.isGeneratingPdf) return;
    this.isGeneratingPdf = true;
    this.pdfError = null;
    if (this._objectUrl) {
      URL.revokeObjectURL(this._objectUrl);
      this._objectUrl = null;
    }
    this._pdfBytes = null;
    this.render(); // update loading state

    try {
      const { documentEngine } = await import("../../../infrastructure/documents/document-engine.js");
      await import("../../../infrastructure/documents/builders/index.js");

      this._pdfBytes = await documentEngine.generatePdfBytes(this._snapshot);
      const blob = new Blob([this._pdfBytes as BlobPart], { type: "application/pdf" });
      this._objectUrl = URL.createObjectURL(blob);
      console.log("[PDF-PREVIEW]", {
        module: this._snapshot?.type,
        blobSize: blob.size,
        blobUrl: this._objectUrl
      });
      this.isGeneratingPdf = false;
      this.render();
    } catch (e: any) {
      this.isGeneratingPdf = false;
      console.error(`[PDF] Falha ao gerar documento ${this._snapshot?.type} ${this._snapshot?.id}`, e);
      this.pdfError = e instanceof AppError ? (e.safeMessage || e.message) : (e?.message || "Não foi possível gerar a prévia do PDF.");
      this.render();
    }
  }

  render() {
    let contentHtml = "";

    if (this.isGeneratingPdf) {
      contentHtml = `
        <div style="display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 56px 24px; height: 100%; justify-content: center; color: var(--color-text-muted);">
          <svg class="spinner spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Gerando PDF...">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <span class="text-body-small" style="font-weight: 600;">Gerando prévia oficial do relatório...</span>
        </div>
      `;
    } else if (this.pdfError) {
      contentHtml = `
        <div style="color: var(--status-danger-solid); display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 40px; text-align: center; height: 100%; justify-content: center;">
          <div style="font-weight: 600; font-size: 1rem;">Não foi possível compilar o PDF integrado</div>
          <div style="font-size: 0.85rem; color: var(--color-graphite-300); max-width: 480px;">${this.pdfError}</div>
        </div>
      `;
    } else if (this._objectUrl) {
      contentHtml = `
        <iframe id="pdf-preview-iframe" src="${this._objectUrl}" style="width: 100%; height: 100%; border: none; box-shadow: 0 4px 12px rgba(0,0,0,0.2);"></iframe>
      `;
    }

    this.innerHTML = `
      <style>
        .action-icon-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 11px;
          border-radius: var(--radius-sm);
          border: 1px solid var(--color-border-default);
          background: var(--color-surface-primary);
          color: var(--color-text-primary);
          font-size: 0.8125rem;
          font-weight: 500;
          cursor: pointer;
          transition: all var(--transition-fast);
        }
        .action-icon-btn:hover { background: var(--color-surface-secondary); }
        .action-icon-btn.primary { background: var(--color-gold-main); border-color: var(--color-gold-main); color: var(--color-graphite-base); font-weight: 600; }
        .action-icon-btn.primary:hover { background: var(--color-gold-light); border-color: var(--color-gold-light); }
      </style>
      <div style="display: flex; flex-direction: column; width: 100%; height: 100%; background: var(--color-graphite-800); position: relative;">
        <!-- TOOLBAR -->
        <div style="background: var(--color-primary); padding: 12px 16px; border-bottom: 1px solid var(--color-graphite-700); display: flex; align-items: center; justify-content: space-between; z-index: 10;">
          <div style="display: flex; align-items: center; gap: 16px;">
            <div style="font-weight: 700; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
              ${this._snapshot?.type || "RI"} • ${this._snapshot?.documentNumber || this._snapshot?.id || "Preview"}
            </div>
            <div style="display: flex; gap: 4px;">
              <span style="color: var(--color-graphite-400); font-size: 0.85rem; padding: 4px 8px; background: var(--color-graphite-700); border-radius: 4px;">PDF Oficial Integrado</span>
            </div>
          </div>
          <div style="display: flex; gap: 8px; align-items: center;">
            <button type="button" class="action-icon-btn" id="btn-print-preview" title="Imprimir Laudo" ${!this._objectUrl ? "disabled" : ""}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
              Imprimir
            </button>
            <button type="button" class="action-icon-btn primary" id="btn-download-pdf" ${!this._pdfBytes ? "disabled" : ""}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Baixar PDF
            </button>
          </div>
        </div>

        <div id="pdf-render-area" style="flex: 1; overflow: hidden; background: var(--ia-bg-inset, #080d1a); position: relative;">
          ${contentHtml}
        </div>
      </div>
    `;

    this.attachEvents();
  }

  private attachEvents() {
    const iframe = this.querySelector("#pdf-preview-iframe") as HTMLIFrameElement | null;
    if (iframe) {
      iframe.addEventListener("load", () => {
        console.log("[PDF-PREVIEW]", { module: this._snapshot?.type, iframeLoaded: true });
      });
      iframe.addEventListener("error", (err) => {
        console.error("[PDF-PREVIEW-IFRAME-ERROR]", { module: this._snapshot?.type, error: err });
      });
    }

    this.querySelector("#btn-print-preview")?.addEventListener("click", () => {
      if (iframe?.contentWindow) {
        iframe.contentWindow.print();
      }
    });

    this.querySelector("#btn-download-pdf")?.addEventListener("click", async () => {
      if (!this._snapshot || !this._pdfBytes) return;
      try {
        const { triggerBrowserDownload } = await import("../../../infrastructure/documents/document-engine.js");
        const docNum = this._snapshot.documentNumber || `${this._snapshot.type}_${this._snapshot.id}`;
        let finalDocNum = docNum.startsWith("RELATÓRIO DE INTEGRIDADE") ? docNum : (docNum.startsWith("RI-") ? `RELATÓRIO DE INTEGRIDADE ${docNum}` : docNum);

        if ((this._snapshot.type === "RI" || this._snapshot.type === "RELATORIO_INTEGRIDADE") && !finalDocNum.includes("RELATÓRIO DE INTEGRIDADE")) {
          finalDocNum = `RELATÓRIO DE INTEGRIDADE ${finalDocNum}`;
        }

        // P0-02 V5 Bloco B: Nome do arquivo PDF deve identificar claramente MINUTA — PARADA / ROTINA.
        // O trackingNumber interno permanece inalterado; apenas o nome do arquivo recebe o prefixo.
        const snapshotData = (this._snapshot as any).data || this._snapshot;
        const execType = (snapshotData?.executionType || '').toString().toUpperCase();
        if (this._snapshot?.type === 'MINUTA' && (execType === 'PARADA' || execType === 'ROTINA')) {
          finalDocNum = `MINUTA_${execType}_${finalDocNum}`;
        }

        triggerBrowserDownload(this._pdfBytes, `${finalDocNum}.pdf`);
      } catch (e: any) {
        alert("Erro ao baixar PDF: " + e.message);
      }
    });
  }
}

if (!customElements.get("app-pdf-preview")) {
  customElements.define("app-pdf-preview", PdfPreview);
}


